import React, { useState, useEffect } from 'react';

import WysiwygEditor from '../WysiwygEditor';
import WinnerSelector from './WinnerSelector';
import Swal from 'sweetalert2';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Plus,
  Pencil,
  Trash2,
  ArrowLeft,
  X as XIcon,
  Target,
  FileText,
  ClipboardList,
  CircleQuestionMark,
  Images
} from 'lucide-react';

// For static assets, use ASSET_URL in dev, hostname in prod
const ASSET_BASE_URL = process.env.REACT_APP_ASSET_URL || process.env.REACT_APP_HOSTNAME || '';

// Sortable FAQ Item Component
function SortableFaqItem({ faq, index, onUpdate, onDelete, isExpanded, onToggle }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: faq.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border border-gray-200 rounded-lg overflow-hidden ${isDragging ? 'z-50 shadow-lg' : ''}`}
    >
      {/* Collapsed Header */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center space-x-3 flex-1">
            <button
              type="button"
              {...attributes}
              {...listeners}
              className="p-1 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
              title="Drag to reorder"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z"/>
              </svg>
            </button>

            <button
              type="button"
              onClick={() => onToggle(faq.id)}
              className="flex items-center space-x-2 flex-1 text-left hover:text-teal-600 transition-colors"
            >
              <svg
                className={`h-5 w-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <span className="font-medium text-gray-700">
                {faq.question || `Question ${index + 1}`}
              </span>
            </button>
          </div>

          <button
            type="button"
            onClick={() => onDelete(faq.id)}
            className="p-2 text-red-500 hover:text-red-700 transition-colors"
            title="Delete FAQ"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-4 space-y-4 bg-white">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Question
            </label>
            <input
              type="text"
              value={faq.question}
              onChange={(e) => onUpdate(faq.id, { question: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="Enter question..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Answer
            </label>
            <WysiwygEditor
              value={faq.answer || ''}
              onChange={(html) => onUpdate(faq.id, { answer: html })}
              placeholder="Enter answer..."
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Sortable Section Item Component for drag and drop
function SortableSectionItem({ section, onEdit, onDuplicate, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const sectionTypes = {
    banner: { title: 'Banner', icon: Target, color: 'bg-blue-100 text-blue-800', bgColor: 'bg-blue-50' },
    content: { title: 'Content', icon: FileText, color: 'bg-purple-100 text-purple-800', bgColor: 'bg-purple-50' },
    forms: { title: 'Form', icon: ClipboardList, color: 'bg-green-100 text-green-800', bgColor: 'bg-green-50' },
    faqs: { title: 'FAQs', icon: CircleQuestionMark, color: 'bg-yellow-100 text-yellow-800', bgColor: 'bg-yellow-50' },
    gallery: { title: 'Gallery', icon: Images, color: 'bg-pink-100 text-pink-800', bgColor: 'bg-pink-50' }
  };

  const sectionInfo = sectionTypes[section.type] || sectionTypes.content;
  const SectionIcon = sectionInfo.icon;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${sectionInfo.bgColor} border-2 border-gray-300 rounded-xl p-6 hover:border-teal-400 transition-all duration-200 group ${isDragging ? 'z-50' : ''}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-3">
            <span className={`px-3 py-1 rounded-full text-sm font-medium inline-flex items-center gap-1.5 ${sectionInfo.color}`}>
              <SectionIcon className="w-3.5 h-3.5" />
              {sectionInfo.title}
            </span>
            <span className="text-sm text-gray-500">#{section.sort_order}</span>
          </div>
          
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-gray-900">
              {section.about_header || section.faqs_header || `${sectionInfo.title} Section`}
            </h3>
            
            {section.about_description && (
              <p className="text-sm text-gray-600 line-clamp-2">
                {section.about_description.replace(/<[^>]*>/g, '').substring(0, 100)}...
              </p>
            )}
            
            {section.faqs_description && (
              <p className="text-sm text-gray-600 line-clamp-2">
                {section.faqs_description.replace(/<[^>]*>/g, '').substring(0, 100)}...
              </p>
            )}
            
            {section.background_image_url && (
              <div className="mt-3">
                <img
                  src={`${ASSET_BASE_URL}${section.background_image_url}`}
                  alt="Section background"
                  className="h-20 w-32 object-cover rounded-lg"
                />
              </div>
            )}
          </div>
        </div>
        
        <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            {...attributes}
            {...listeners}
            className="p-2 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
            title="Drag to reorder"
          >
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z"/>
            </svg>
          </button>
          <button
            onClick={() => onEdit(section)}
            className="p-2 text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded-lg"
            title="Edit section"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            onClick={() => onDuplicate(section)}
            className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg"
            title="Duplicate section"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </button>
          <button
            onClick={() => {
              if (window.confirm('Are you sure you want to delete this section?')) {
                onDelete(section.id);
              }
            }}
            className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg"
            title="Delete section"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

const SectionOverview = ({ 
  campaignId, 
  sections = [], 
  pageType = 'landing_page',
  onAddSection, 
  onDeleteSection,
  onDuplicateSection,
  onUpdateSection,
  api,
  isWinnerPage = false,
  resourceBase = '/admin/campaigns',
  sectionsPath = 'campaign-sections',
  sectionTemplatesEndpoint = '/admin/campaigns/section-templates'
}) => {
  const [currentView, setCurrentView] = useState('overview'); // 'overview' or 'editing'
  const [editingSection, setEditingSection] = useState(null);
  const [uploadingBackground, setUploadingBackground] = useState(false);
  const [sectionFaqs, setSectionFaqs] = useState([]);
  const [sectionGallery, setSectionGallery] = useState([]);
  const [uploadingGalleryImage, setUploadingGalleryImage] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [contentTemplates, setContentTemplates] = useState([]);
  const [formsTemplates, setFormsTemplates] = useState([]);
  const [faqsTemplates, setFaqsTemplates] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [expandedFaqId, setExpandedFaqId] = useState(null);
  const [selectedWinner, setSelectedWinner] = useState(null);
  const [contentSettingsTab, setContentSettingsTab] = useState('layout'); // 'layout' or 'background'
  const resourceRoot = `${resourceBase}/${campaignId}`;
  const sectionsRoot = `${resourceRoot}/${sectionsPath}`;

  useEffect(() => {
    const fetchOverallWinner = async () => {
      if (!isWinnerPage || !campaignId) return;
      try {
        const response = await api.get(`${resourceRoot}/overall-winner`);
        if (response.data?.winner) {
          setSelectedWinner(response.data.winner);
        } else {
          setSelectedWinner(null);
        }
      } catch (error) {
        console.error('Failed to fetch overall winner:', error);
      }
    };

    fetchOverallWinner();
  }, [api, campaignId, isWinnerPage, resourceRoot]);

  // DnD sensors for section reordering
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const sectionTypes = {
    banner: { title: 'Banner', icon: Target, color: 'bg-blue-100 text-blue-800', bgColor: 'bg-blue-50' },
    content: { title: 'Content', icon: FileText, color: 'bg-purple-100 text-purple-800', bgColor: 'bg-purple-50' },
    forms: { title: 'Form', icon: ClipboardList, color: 'bg-green-100 text-green-800', bgColor: 'bg-green-50' },
    faqs: { title: 'FAQs', icon: CircleQuestionMark, color: 'bg-yellow-100 text-yellow-800', bgColor: 'bg-yellow-50' },
    gallery: { title: 'Gallery', icon: Images, color: 'bg-pink-100 text-pink-800', bgColor: 'bg-pink-50' }
  };

  // Fetch templates based on page type
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await api.get(sectionTemplatesEndpoint, {
          params: { page_type: pageType }
        });
        const contentTemplates = response.data.templates.filter(t => t.type === 'content');
        const formsTemplates = response.data.templates.filter(t => t.type === 'forms');
        const faqsTemplates = response.data.templates.filter(t => t.type === 'faqs');
        setContentTemplates(contentTemplates);
        setFormsTemplates(formsTemplates);
        setFaqsTemplates(faqsTemplates);
      } catch (error) {
        // Set default templates as fallback based on page type
        const defaultContentTemplates = pageType === 'landing_page' ? [
          { id: 1, type: 'content', name: 'Content with Forms', codename: 'content-with-forms' },
          { id: 2, type: 'content', name: 'Content with Background', codename: 'content-with-background' },
          { id: 3, type: 'content', name: 'Content with Background Overlay', codename: 'content-with-background-overlay' }
        ] : [
          { id: 2, type: 'content', name: 'Content with Background', codename: 'content-with-background' },
          { id: 3, type: 'content', name: 'Content with Background Overlay', codename: 'content-with-background-overlay' }
        ];
        
        const defaultFormsTemplates = pageType === 'landing_page' ? [
          { id: 4, type: 'forms', name: 'Forms Normal', codename: 'forms-normal' },
          { id: 5, type: 'forms', name: 'Forms with Background', codename: 'forms-with-background' }
        ] : [];
        
        const defaultFaqsTemplates = [
          { id: 6, type: 'faqs', name: 'FAQs', codename: 'faqs' },
          { id: 7, type: 'faqs', name: 'FAQs V2', codename: 'faqs-v2' },
          { id: 8, type: 'faqs', name: 'FAQs with Accent Color', codename: 'faqs-with-accent-color' }
        ];
        
        setContentTemplates(defaultContentTemplates);
        setFormsTemplates(defaultFormsTemplates);
        setFaqsTemplates(defaultFaqsTemplates);
      }
    };
    fetchTemplates();
  }, [api, pageType, sectionTemplatesEndpoint]);

  // Handle drag end for section reordering
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sections.findIndex(section => section.id === active.id);
    const newIndex = sections.findIndex(section => section.id === over.id);
    
    const reorderedSections = arrayMove(sections, oldIndex, newIndex);
    
    // Update sort orders
    const updatedSections = reorderedSections.map((section, index) => ({
      ...section,
      sort_order: index
    }));
    
    onUpdateSection(updatedSections);

    // Save to backend
    try {
      const updates = updatedSections.map((section, index) => ({
        id: section.id,
        sort_order: index
      }));

      await api.patch(`${sectionsRoot}/reorder`, {
        sections: updates
      });
    } catch (error) {
      // Could revert the order here if needed
    }
  };

  const handleEditSection = async (section) => {
    setEditingSection({ ...section });
    setCurrentView('editing');
    
    // Load section-specific data
    if (section.type === 'faqs') {
      try {
        const response = await api.get(`${sectionsRoot}/${section.id}/faqs`);
        setSectionFaqs(response.data.faqs || []);
      } catch (error) {
      }
    }
    
    if (section.type === 'gallery') {
      try {
        const response = await api.get(`${sectionsRoot}/${section.id}/gallery`);
        setSectionGallery(response.data.gallery || []);
      } catch (error) {
      }
    }
  };

  const handleBackToOverview = () => {
    setCurrentView('overview');
    setEditingSection(null);
    setSectionFaqs([]);
    setSectionGallery([]);
  };

  const handleSectionUpdate = (field, value) => {
    setEditingSection(prev => ({ ...prev, [field]: value }));
    
    // Also update the main sections array
    const updatedSections = sections.map(section => 
      section.id === editingSection.id ? { ...section, [field]: value } : section
    );
    onUpdateSection(updatedSections);
  };

  const handleWinnerSelect = async (winner) => {
    try {
      setSelectedWinner(winner);
      
      // Save winner to backend
      const response = await api.post(`${resourceRoot}/save-overall-winner`, {
        entryId: winner.id,
        position: 1
      });
      
      if (response.data.success) {
        if (response.data.winner) {
          setSelectedWinner(response.data.winner);
        }
        setShowSuccess(true);
        setSuccessMessage('Winner saved successfully!');
        setTimeout(() => setShowSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Failed to save winner:', error);
      setShowError(true);
      setErrorMessage('Failed to save winner. Please try again.');
      setTimeout(() => setShowError(false), 5000);
    }
  };

  const handleWinnerClear = async () => {
    if (!selectedWinner) return;
    
    try {
      const response = await api.post(`${resourceRoot}/clear-overall-winner`);
      
      if (response.data.success) {
        setSelectedWinner(null);
        setShowSuccess(true);
        setSuccessMessage('Winner removed successfully!');
        setTimeout(() => setShowSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Failed to remove winner:', error);
      setShowError(true);
      setErrorMessage('Failed to remove winner. Please try again.');
      setTimeout(() => setShowError(false), 5000);
    }
  };

  const handleBackgroundUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    const validVideoTypes = ['video/mp4', 'video/webm', 'video/ogg'];
    const validTypes = [...validImageTypes, ...validVideoTypes];
    
    if (!validTypes.includes(file.type)) {
      alert('Please upload an image (JPG, PNG, GIF) or video (MP4, WebM, OGG) file');
      return;
    }
    
    const maxSize = validVideoTypes.includes(file.type) ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(`File size must be less than ${validVideoTypes.includes(file.type) ? '50MB' : '10MB'}`);
      return;
    }

    setUploadingBackground(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`${sectionsRoot}/${editingSection.id}/upload-background`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      handleSectionUpdate('background_image_url', response.data.fileUrl);
      e.target.value = '';
    } catch (error) {
      alert('Failed to upload background: ' + (error.response?.data?.error || error.message));
    } finally {
      setUploadingBackground(false);
    }
  };

  const handleContentBackgroundUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    const validVideoTypes = ['video/mp4', 'video/webm', 'video/ogg'];
    const validTypes = [...validImageTypes, ...validVideoTypes];
    
    if (!validTypes.includes(file.type)) {
      alert('Please upload an image (JPG, PNG, GIF) or video (MP4, WebM, OGG) file');
      return;
    }
    
    const maxSize = validVideoTypes.includes(file.type) ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(`File size must be less than ${validVideoTypes.includes(file.type) ? '50MB' : '10MB'}`);
      return;
    }
    setUploadingBackground(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`${sectionsRoot}/${editingSection.id}/upload-content-background`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      handleSectionUpdate('background_image_url', response.data.fileUrl);
      e.target.value = '';
    } catch (error) {
      alert('Failed to upload content background: ' + (error.response?.data?.error || error.message));
    } finally {
      setUploadingBackground(false);
    }
  };

  const handleSaveSection = async () => {
    if (!editingSection || isSaving) return;
    
    setIsSaving(true);
    try {
      
      await api.put(`${sectionsRoot}/${editingSection.id}`, {
        data: editingSection,
        template_codename: editingSection.template_codename
      });
      
      // If this is a FAQ section, save all FAQs
      if (editingSection.type === 'faqs' && sectionFaqs.length > 0) {
        // Save each FAQ
        for (const faq of sectionFaqs) {
          try {
            await api.put(`${sectionsRoot}/${editingSection.id}/faqs/${faq.id}`, {
              question: faq.question,
              answer: faq.answer,
              sort_order: faq.sort_order
            });
          } catch (error) {
            alert(`Failed to save FAQ: ${faq.question}`);
          }
        }
      }
      
      // Refresh the section data after save
      const updatedSections = sections.map(section => 
        section.id === editingSection.id ? editingSection : section
      );
      onUpdateSection(updatedSections);
      
      // Show success notification
      setSuccessMessage('Section saved successfully!');
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
      // Don't close the edit view immediately, let user decide when to go back
    } catch (error) {
      alert('Failed to save section: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  // FAQ Management Functions
  const handleAddFaq = async () => {
    const newFaq = {
      question: 'New Question',
      answer: 'Enter your answer here',
      sort_order: sectionFaqs.length
    };

    try {
      const response = await api.post(`${sectionsRoot}/${editingSection.id}/faqs`, newFaq);
      setSectionFaqs(prev => [...prev, response.data.faq]);
      // Auto-expand the newly added FAQ
      setExpandedFaqId(response.data.faq.id);
    } catch (error) {
      setErrorMessage(error.response?.data?.error || error.message || 'Failed to add FAQ');
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 5000);
    }
  };

  const handleUpdateFaq = (faqId, data) => {
    setSectionFaqs(prev => prev.map(faq =>
      faq.id === faqId ? { ...faq, ...data } : faq
    ));
  };

  const handleDeleteFaq = async (faqId) => {
    const result = await Swal.fire({
      title: 'Delete FAQ?',
      text: 'Are you sure you want to delete this FAQ? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    try {
      await api.delete(`${sectionsRoot}/${editingSection.id}/faqs/${faqId}`);
      setSectionFaqs(prev => prev.filter(faq => faq.id !== faqId));
    } catch (error) {
      setErrorMessage('Failed to delete FAQ');
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 5000);
    }
  };

  const handleReorderFaqs = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sectionFaqs.findIndex(faq => faq.id === active.id);
    const newIndex = sectionFaqs.findIndex(faq => faq.id === over.id);
    
    const reorderedFaqs = arrayMove(sectionFaqs, oldIndex, newIndex);
    setSectionFaqs(reorderedFaqs);

    // Update sort orders
    const updates = reorderedFaqs.map((faq, index) => ({
      id: faq.id,
      sort_order: index
    }));

    try {
      await api.patch(`${sectionsRoot}/${editingSection.id}/faqs/reorder`, {
        faqs: updates
      });
    } catch (error) {
    }
  };

  // Gallery Management Functions
  const handleGalleryImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!validImageTypes.includes(file.type)) {
      alert('Please upload an image file (JPG, PNG, GIF)');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    setUploadingGalleryImage(true);
    
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await api.post(`${sectionsRoot}/${editingSection.id}/gallery`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSectionGallery(prev => [...prev, response.data.galleryItem]);
      e.target.value = '';
    } catch (error) {
      alert('Failed to upload image: ' + (error.response?.data?.error || error.message));
    } finally {
      setUploadingGalleryImage(false);
    }
  };

  const handleDeleteGalleryImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    
    try {
      await api.delete(`${sectionsRoot}/${editingSection.id}/gallery/${imageId}`);
      setSectionGallery(prev => prev.filter(image => image.id !== imageId));
    } catch (error) {
      alert('Failed to delete image: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleUpdateGalleryImage = async (imageId, data) => {
    try {
      await api.put(`${sectionsRoot}/${editingSection.id}/gallery/${imageId}`, data);
      setSectionGallery(prev => prev.map(image => 
        image.id === imageId ? { ...image, ...data } : image
      ));
    } catch (error) {
      alert('Failed to update image');
    }
  };

  // Overview View - Show all sections
  if (currentView === 'overview') {
    return (
      <div className="space-y-6">
        {/* Success Notification */}
        {showSuccess && (
          <div className="fixed top-4 right-4 z-50 animate-fadeInRight">
            <div className="bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3">
              <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">{successMessage}</span>
            </div>
          </div>
        )}

        {/* Error Notification */}
        {showError && (
          <div className="fixed top-4 right-4 z-50 animate-fadeInRight">
            <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3">
              <svg className="h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">{errorMessage}</span>
            </div>
          </div>
        )}
        
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold text-gray-900">Page Sections</h3>
          <button
            onClick={onAddSection}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Add Section</span>
          </button>
        </div>
        
        {sections.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sections.map(section => section.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-4">
                {sections
                  .sort((a, b) => a.sort_order - b.sort_order)
                  .map(section => (
                    <SortableSectionItem
                      key={section.id}
                      section={section}
                      onEdit={handleEditSection}
                      onDuplicate={onDuplicateSection}
                      onDelete={onDeleteSection}
                    />
                  ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <div className="mb-4">
              <svg className="h-16 w-16 mx-auto text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No sections created yet</h3>
            <p className="text-gray-500 mb-6">Create your first section to start building your campaign page.</p>
            <button
              onClick={onAddSection}
              className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium"
            >
              Create First Section
            </button>
          </div>
        )}
      </div>
    );
  }

  // Single Section Editing View
  if (currentView === 'editing' && editingSection) {
    const sectionInfo = sectionTypes[editingSection.type] || sectionTypes.content;
    const SectionIcon = sectionInfo.icon;

    return (
      <div className="space-y-6">
        {/* Success Notification */}
        {showSuccess && (
          <div className="fixed top-4 right-4 z-50 animate-fadeInRight">
            <div className="bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3">
              <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">{successMessage}</span>
            </div>
          </div>
        )}

        {/* Error Notification */}
        {showError && (
          <div className="fixed top-4 right-4 z-50 animate-fadeInRight">
            <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3">
              <svg className="h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">{errorMessage}</span>
            </div>
          </div>
        )}
        
        {/* Header with back button */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleBackToOverview}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center space-x-3">
              <span className={`px-3 py-1 rounded-full text-sm font-medium inline-flex items-center gap-1.5 ${sectionInfo.color}`}>
                <SectionIcon className="w-3.5 h-3.5" />
                {sectionInfo.title}
              </span>
              <h3 className="text-xl font-bold text-gray-900">
                {editingSection.about_header || editingSection.faqs_header || `Edit ${sectionInfo.title} Section`}
              </h3>
            </div>
          </div>
          <button
            onClick={handleSaveSection}
            disabled={isSaving}
            className={`px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 ${
              isSaving 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-teal-600 hover:bg-teal-700 cursor-pointer'
            } text-white`}
          >
            {isSaving && (
              <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            <span>{isSaving ? 'Saving...' : 'Save Section'}</span>
          </button>
        </div>

        {/* Section Editing Form */}
        <div className={`${sectionInfo.bgColor} border border-gray-300 rounded-xl p-6`}>
          <div className="space-y-6">
            
            {/* Background Settings for banner only */}
            {editingSection.type === 'banner' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h5 className="text-md font-medium text-gray-700 mb-4">Background Settings</h5>
                  
                  {editingSection.background_image_url && (
                    <div className="mb-4">
                      {editingSection.background_image_url.match(/\\.(mp4|webm|ogg)$/i) ? (
                        <video 
                          src={`${ASSET_BASE_URL}${editingSection.background_image_url}`} 
                          className="h-48 w-full object-cover rounded-lg max-w-md"
                          controls
                          muted
                        />
                      ) : (
                        <img 
                          src={`${ASSET_BASE_URL}${editingSection.background_image_url}`} 
                          alt="Background" 
                          className="h-48 w-full object-cover rounded-lg max-w-md"
                        />
                      )}
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-4 mb-4">
                    <label className={`cursor-pointer px-4 py-2 rounded-lg font-medium ${
                      uploadingBackground ? 'bg-gray-300 cursor-not-allowed' : 'bg-gray-200 hover:bg-gray-300'
                    } text-gray-700`}>
                      <span>{uploadingBackground ? 'Uploading...' : 'Upload Background'}</span>
                      <input
                        type="file"
                        className="sr-only"
                        accept="image/jpeg,image/jpg,image/png,image/gif,video/mp4,video/webm,video/ogg"
                        onChange={handleBackgroundUpload}
                        disabled={uploadingBackground}
                      />
                    </label>
                    {editingSection.background_image_url && (
                      <button
                        type="button"
                        onClick={() => handleSectionUpdate('background_image_url', '')}
                        className="text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="full_width_background"
                      checked={editingSection.full_width_background}
                      onChange={(e) => handleSectionUpdate('full_width_background', e.target.checked)}
                      className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <label htmlFor="full_width_background" className="ml-2 text-sm text-gray-700">
                      Full width background
                    </label>
                  </div>
                </div>

                {/* Banner Dimensions Info */}
                <div>
                  <h5 className="text-md font-medium text-gray-700 mb-4">Banner Dimensions</h5>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div>
                      <h6 className="text-sm font-medium text-gray-600 mb-2">Height (Fixed)</h6>
                      <div className="space-y-1 text-sm text-gray-500">
                        <div>• Mobile: <span className="font-mono">320px</span> (container width)</div>
                        <div>• Tablet: <span className="font-mono">384px</span> (container width)</div>
                        <div>• Desktop: <span className="font-mono">448px</span> (container width)</div>
                        <div className="text-xs text-gray-400 mt-1">Height scales with viewport width</div>
                      </div>
                    </div>
                    
                    <div>
                      <h6 className="text-sm font-medium text-gray-600 mb-2">Width (Variable)</h6>
                      <div className="space-y-1 text-sm text-gray-500">
                        <div>• Default: Container max-width</div>
                        <div>• Full Width: ~100% viewport</div>
                        <div className="text-xs text-gray-400 mt-2">
                          Images scale with <code className="bg-gray-200 px-1 rounded">bg-contain</code>
                        </div>
                      </div>
                    </div>

                    <div className="pt-2 border-t border-gray-200">
                      <h6 className="text-sm font-medium text-gray-600 mb-2">Recommended Image Size</h6>
                      <div className="text-sm text-gray-500">
                        <div>• Aspect Ratio: <span className="font-mono">2.12:1</span> (2933×1382)</div>
                        <div>• Resolution: <span className="font-mono">2933x1382px</span> minimum</div>
                        <div className="text-xs text-gray-400 mt-1">
                          Higher resolution images will be automatically scaled
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}


            {/* Content Settings for Content (with template selection) */}
            {editingSection.type === 'content' && (
              <div>
                <h4 className="text-lg font-medium text-gray-700 mb-6">Content Settings</h4>
                
                {/* Tabs */}
                <div className="border-b border-gray-200 mb-6">
                  <nav className="-mb-px flex space-x-8">
                    <button
                      onClick={() => setContentSettingsTab('layout')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm ${
                        contentSettingsTab === 'layout'
                          ? 'border-teal-500 text-teal-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Layout & Sections
                    </button>
                    <button
                      onClick={() => setContentSettingsTab('background')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm ${
                        contentSettingsTab === 'background'
                          ? 'border-teal-500 text-teal-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Background Image
                    </button>
                  </nav>
                </div>

                {/* Tab Content */}
                {contentSettingsTab === 'layout' && (
                  <div className="space-y-6">
                    {/* Template Selection - Dynamic based on available templates */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">Content Layout</label>
                      {contentTemplates.length === 0 ? (
                        <p className="text-sm text-gray-500 italic">Loading templates...</p>
                      ) : (
                        <div className={`grid grid-cols-1 ${contentTemplates.length > 2 ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-4`}>
                          {contentTemplates.map((template) => {
                          // Check if this template is selected
                          const isSelected = editingSection.template_codename === template.codename || 
                            (!editingSection.template_codename && 
                             ((template.codename === 'content-with-forms' && editingSection.about_with_form === true) ||
                              (template.codename === 'content-with-background' && editingSection.about_with_form !== true)));
                          
                          // Get description based on template codename
                          const getDescription = (codename) => {
                            switch(codename) {
                              case 'content-with-forms':
                                return 'Section content with entry form integration';
                              case 'content-with-background':
                                return 'Full-width background with text overlay';
                              case 'content-with-background-overlay':
                                return 'Full-width background with centered content box';
                              case 'content-without-background':
                                return 'Simple content section, same width as banner';
                              default:
                                return 'Content template';
                            }
                          };

                          return (
                            <div 
                              key={template.id}
                              className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                isSelected
                                  ? 'border-teal-500 bg-teal-50' 
                                  : 'border-gray-300 hover:border-gray-400'
                              }`}
                              onClick={() => {
                                handleSectionUpdate('template_codename', template.codename);
                                // Keep backward compatibility
                                if (template.codename === 'content-with-forms') {
                                  handleSectionUpdate('about_with_form', true);
                                } else {
                                  handleSectionUpdate('about_with_form', false);
                                }
                              }}
                            >
                              <div className="flex items-center">
                                <input
                                  type="radio"
                                  name="content_layout"
                                  value={template.codename}
                                  checked={isSelected}
                                  onChange={() => {}}
                                  className="h-4 w-4 text-teal-600 border-gray-300 focus:ring-teal-500"
                                />
                                <div className="ml-3">
                                  <h5 className="font-medium text-gray-900">{template.name}</h5>
                                  <p className="text-sm text-gray-600">{getDescription(template.codename)}</p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      )}
                    </div>

                    {/* Common Fields */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Section Header</label>
                        <input
                          type="text"
                          value={editingSection.about_header || ''}
                          onChange={(e) => handleSectionUpdate('about_header', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                          placeholder="Enter section header..."
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Section Description</label>
                        <WysiwygEditor
                          value={editingSection.about_description || ''}
                          onChange={(html) => handleSectionUpdate('about_description', html)}
                          placeholder="Enter section description..."
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">YouTube Video URL</label>
                      <input
                        type="url"
                        value={editingSection.youtube_video_url || ''}
                        onChange={(e) => handleSectionUpdate('youtube_video_url', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                        placeholder="https://www.youtube.com/watch?v=..."
                        spellCheck={false}
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        Optional. Supports full YouTube links or shortened youtu.be links. Leave blank to hide the video.
                      </p>
                      {editingSection.youtube_video_url && (
                        <button
                          type="button"
                          onClick={() => handleSectionUpdate('youtube_video_url', '')}
                          className="mt-2 text-sm text-red-600 hover:text-red-700"
                        >
                          Remove video link
                        </button>
                      )}
                    </div>

                    {/* Winner Selection (only for winner page) */}
                    {isWinnerPage && (
                      <div className="mt-6">
                        <h5 className="text-md font-medium text-gray-700 mb-4">Winner Selection</h5>
                        <WinnerSelector
                          campaignId={campaignId}
                          api={api}
                          selectedWinner={selectedWinner}
                          onWinnerSelect={handleWinnerSelect}
                          onWinnerClear={handleWinnerClear}
                        />
                      </div>
                    )}

                    {/* Template-specific Settings (non-background) */}
                    {(() => {
                      // Determine which template is active
                      let activeTemplate = editingSection.template_codename;
                      if (!activeTemplate && editingSection.about_with_form === true) {
                        activeTemplate = 'content-with-forms';
                      } else if (!activeTemplate && editingSection.about_with_form === false) {
                        activeTemplate = 'content-with-background';
                      }

                      if (activeTemplate === 'content-with-forms') {
                        return (
                          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <h5 className="font-medium text-blue-900 mb-3">Form Integration Settings</h5>
                            <p className="text-sm text-blue-800">
                              The entry form will be displayed alongside the content in this section.
                            </p>
                            <p className="text-xs text-blue-600 mt-1">
                              Make sure you have configured a form in the Forms tab for it to appear.
                            </p>
                          </div>
                        );
                      }

                      return null;
                    })()}
                  </div>
                )}

                {contentSettingsTab === 'background' && (
                  <div className="space-y-6">
                    {(() => {
                      // Determine which template is active
                      let activeTemplate = editingSection.template_codename;
                      if (!activeTemplate && editingSection.about_with_form === true) {
                        activeTemplate = 'content-with-forms';
                      } else if (!activeTemplate && editingSection.about_with_form === false) {
                        activeTemplate = 'content-with-background';
                      }

                      // Only show background settings for templates that support it
                      if (activeTemplate === 'content-with-background' || activeTemplate === 'content-with-background-overlay') {
                        return (
                          <div className={`p-4 ${activeTemplate === 'content-with-background' ? 'bg-purple-50 border-purple-200' : 'bg-indigo-50 border-indigo-200'} border rounded-lg`}>
                            <h5 className={`font-medium ${activeTemplate === 'content-with-background' ? 'text-purple-900' : 'text-indigo-900'} mb-3`}>
                              {activeTemplate === 'content-with-background' ? 'Background Image Settings' : 'Background Overlay Settings'}
                            </h5>
                            <div className="space-y-4">
                              {editingSection.background_image_url && (
                                <div className="mb-4">
                                  {editingSection.background_image_url.match(/\.(mp4|webm|ogg)$/i) ? (
                                    <video 
                                      src={`${ASSET_BASE_URL}${editingSection.background_image_url}`} 
                                      className={`${activeTemplate === 'content-with-background' ? 'h-32 w-48' : 'h-48 w-full'} object-cover rounded-lg border`}
                                      controls
                                      muted
                                    />
                                  ) : (
                                    <img 
                                      src={`${ASSET_BASE_URL}${editingSection.background_image_url}`} 
                                      alt="Background" 
                                      className={`${activeTemplate === 'content-with-background' ? 'h-32 w-48' : 'h-48 w-full'} object-cover rounded-lg border`}
                                    />
                                  )}
                                </div>
                              )}
                              <div className="flex items-center space-x-4">
                                <label className={`cursor-pointer px-4 py-2 rounded-lg font-medium ${
                                  uploadingBackground ? 'bg-gray-300 cursor-not-allowed' : activeTemplate === 'content-with-background' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-indigo-600 hover:bg-indigo-700'
                                } cursor-pointer text-white`}>
                                  <span>{uploadingBackground ? 'Uploading...' : 'Upload Background'}</span>
                                  <input
                                    type="file"
                                    className="sr-only"
                                    accept="image/jpeg,image/jpg,image/png,image/gif,video/mp4,video/webm,video/ogg"
                                    onChange={(e) => handleContentBackgroundUpload(e)}
                                    disabled={uploadingBackground}
                                  />
                                </label>
                                {editingSection.background_image_url && (
                                  <button
                                    type="button"
                                    onClick={() => handleSectionUpdate('background_image_url', '')}
                                    className="text-red-600 hover:text-red-700"
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                              <p className={`text-xs ${activeTemplate === 'content-with-background' ? 'text-purple-600' : 'text-indigo-600'}`}>
                                {activeTemplate === 'content-with-background' 
                                  ? 'Upload an image to display as background for this content section (split layout)'
                                  : 'Upload a full-width background image with text overlay'}
                              </p>
                            </div>
                          </div>
                        );
                      }

                      // Show message if current template doesn't support background images
                      return (
                        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                          <p className="text-sm text-gray-600">
                            Background images are only available for "Content with Background" and "Content with Background Overlay" layouts.
                            {activeTemplate === 'content-with-forms' && ' Switch to a background-enabled layout in the "Layout & Sections" tab to upload a background image.'}
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {/* FAQ Settings */}
            {editingSection.type === 'faqs' && (
              <div>
                <h4 className="text-lg font-medium text-gray-700 mb-6">FAQ Settings</h4>
                
                <div className="space-y-6">
                  {/* Template Selection - Dynamic based on available templates */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">FAQ Layout</label>
                    {faqsTemplates.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">Loading templates...</p>
                    ) : (
                      <div className={`grid grid-cols-1 ${faqsTemplates.length > 2 ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-4`}>
                        {faqsTemplates.map((template) => {
                          // Check if this template is selected
                          const isSelected = editingSection.template_codename === template.codename || 
                            (!editingSection.template_codename && template.codename === 'faqs');
                          
                          // Get description based on template codename
                          const getDescription = (codename) => {
                            switch(codename) {
                              case 'faqs':
                                return 'Default grid layout with header & FAQs';
                              case 'faqs-v2':
                                return 'Centered FAQs with header and description';
                              case 'faqs-with-accent-color':
                                return 'Full-width accent background with FAQs';
                              default:
                                return 'FAQ template';
                            }
                          };

                          return (
                            <div 
                              key={template.id}
                              className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                isSelected
                                  ? 'border-yellow-500 bg-yellow-50' 
                                  : 'border-gray-300 hover:border-gray-400'
                              }`}
                              onClick={() => {
                                handleSectionUpdate('template_codename', template.codename);
                                handleSectionUpdate('template_id', template.id);
                              }}
                            >
                              <div className="flex items-center">
                                <input
                                  type="radio"
                                  name="faqs_layout"
                                  value={template.codename}
                                  checked={isSelected}
                                  onChange={() => {}}
                                  className="h-4 w-4 text-yellow-600 border-gray-300 focus:ring-yellow-500"
                                />
                                <div className="ml-3">
                                  <h5 className="font-medium text-gray-900">{template.name}</h5>
                                  <p className="text-sm text-gray-600">{getDescription(template.codename)}</p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="space-y-8">
                    {/* Section Content - Full Width */}
                    <div className="space-y-4">
                      <h5 className="text-md font-medium text-gray-700">Section Content</h5>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">FAQ Section Header</label>
                          <input
                            type="text"
                            value={editingSection.faqs_header || ''}
                            onChange={(e) => handleSectionUpdate('faqs_header', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                            placeholder="Enter FAQ section header..."
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">FAQ Section Description</label>
                          <WysiwygEditor
                            value={editingSection.faqs_description || ''}
                            onChange={(html) => handleSectionUpdate('faqs_description', html)}
                            placeholder="Enter FAQ section description..."
                          />
                        </div>
                      </div>
                    </div>

                    {/* FAQ Questions - Full Width */}
                    <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h5 className="text-md font-medium text-gray-700">FAQ Questions</h5>
                      <button
                        onClick={handleAddFaq}
                        className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center space-x-2"
                      >
                        <Plus className="h-4 w-4" />
                        <span>Add FAQ</span>
                      </button>
                    </div>
                    
                    {sectionFaqs.length > 0 ? (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleReorderFaqs}
                      >
                        <SortableContext
                          items={sectionFaqs.map(faq => faq.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="space-y-4">
                            {sectionFaqs.map((faq, index) => (
                              <SortableFaqItem
                                key={faq.id}
                                faq={faq}
                                index={index}
                                onUpdate={handleUpdateFaq}
                                onDelete={handleDeleteFaq}
                                isExpanded={expandedFaqId === faq.id}
                                onToggle={(id) => setExpandedFaqId(expandedFaqId === id ? null : id)}
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                    ) : (
                      <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                        No FAQs added yet. Click "Add FAQ" to get started.
                      </div>
                    )}

                    {/* Bottom Add FAQ button for easy access after scrolling */}
                    {sectionFaqs.length > 0 && (
                      <div className="mt-6 flex justify-center">
                        <button
                          onClick={handleAddFaq}
                          className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center space-x-2 shadow-lg"
                        >
                          <Plus className="h-5 w-5" />
                          <span>Add Another FAQ</span>
                        </button>
                      </div>
                    )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Gallery Settings */}
            {editingSection.type === 'gallery' && (
              <div>
                <h4 className="text-lg font-medium text-gray-700 mb-4">Gallery Settings</h4>
                
                <div className="space-y-6">
                  {/* Upload New Image */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h5 className="text-sm font-medium text-gray-600 mb-3">Add New Image</h5>
                    <div className="flex items-center space-x-4">
                      <label className={`cursor-pointer px-4 py-2 rounded-lg font-medium ${
                        uploadingGalleryImage ? 'bg-gray-300 cursor-not-allowed' : 'bg-teal-600 hover:bg-teal-700 cursor-pointer'
                      } text-white`}>
                        <span>{uploadingGalleryImage ? 'Uploading...' : 'Upload Image'}</span>
                        <input
                          type="file"
                          className="sr-only"
                          accept="image/jpeg,image/jpg,image/png,image/gif"
                          onChange={handleGalleryImageUpload}
                          disabled={uploadingGalleryImage}
                        />
                      </label>
                      <p className="text-xs text-gray-500">
                        Supported formats: JPG, PNG, GIF (max 10MB)
                      </p>
                    </div>
                  </div>

                  {/* Gallery Images */}
                  <div>
                    <h5 className="text-md font-medium text-gray-700 mb-4">Gallery Images</h5>
                    {sectionGallery.length > 0 ? (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {sectionGallery.map((image, index) => (
                          <div key={image.id} className="relative group">
                            <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                              <img
                                src={`${ASSET_BASE_URL}${image.image_url}`}
                                alt={image.title || `Gallery image ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            
                            {/* Image overlay with controls */}
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 rounded-lg flex items-center justify-center">
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-2">
                                <button
                                  type="button"
                                  onClick={() => handleDeleteGalleryImage(image.id)}
                                  className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                                  title="Delete image"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                            
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                        No images uploaded yet. Upload your first image above.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Forms Settings */}
            {editingSection.type === 'forms' && (
              <div>
                <h4 className="text-lg font-medium text-gray-700 mb-6">Form Settings</h4>
                
                <div className="space-y-6">
                  {/* Template Selection - Dynamic based on available templates */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Form Layout</label>
                    {formsTemplates.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">Loading templates...</p>
                    ) : (
                      <div className={`grid grid-cols-1 ${formsTemplates.length > 2 ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-4`}>
                        {formsTemplates.map((template) => {
                          // Check if this template is selected
                          const isSelected = editingSection.template_codename === template.codename || 
                            (!editingSection.template_codename && template.codename === 'forms-normal');
                          
                          // Get description based on template codename
                          const getDescription = (codename) => {
                            switch(codename) {
                              case 'forms-normal':
                                return 'Centered form on white background';
                              case 'forms-with-background':
                                return 'Split layout with background image';
                              default:
                                return 'Form template';
                            }
                          };

                          return (
                            <div 
                              key={template.id}
                              className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                isSelected
                                  ? 'border-green-500 bg-green-50' 
                                  : 'border-gray-300 hover:border-gray-400'
                              }`}
                              onClick={() => {
                                handleSectionUpdate('template_codename', template.codename);
                                handleSectionUpdate('template_id', template.id);
                              }}
                            >
                              <div className="flex items-center">
                                <input
                                  type="radio"
                                  name="forms_layout"
                                  value={template.codename}
                                  checked={isSelected}
                                  onChange={() => {}}
                                  className="h-4 w-4 text-green-600 border-gray-300 focus:ring-green-500"
                                />
                                <div className="ml-3">
                                  <h5 className="font-medium text-gray-900">{template.name}</h5>
                                  <p className="text-sm text-gray-600">{getDescription(template.codename)}</p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Template-specific Settings */}
                  {(() => {
                    const activeTemplate = editingSection.template_codename || 'forms-normal';

                    switch(activeTemplate) {
                      case 'forms-normal':
                        return (
                          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                            <h5 className="font-medium text-green-900 mb-3">Standard Form Display</h5>
                            <p className="text-sm text-green-800">
                              The form will be displayed in a clean, centered layout.
                            </p>
                            <p className="text-xs text-green-600 mt-1">
                              Configure form fields in the Forms tab.
                            </p>
                          </div>
                        );

                      case 'forms-with-background':
                        return (
                          <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
                            <h5 className="font-medium text-teal-900 mb-3">Background Image Settings</h5>
                            <div className="space-y-4">
                              {editingSection.background_image_url && (
                                <div className="mb-4">
                                  {editingSection.background_image_url.match(/\.(mp4|webm|ogg)$/i) ? (
                                    <video
                                      src={`${ASSET_BASE_URL}${editingSection.background_image_url}`}
                                      className="h-32 w-48 object-cover rounded-lg border"
                                      controls
                                    />
                                  ) : (
                                    <img 
                                      src={`${ASSET_BASE_URL}${editingSection.background_image_url}`} 
                                      alt="Background" 
                                      className="h-32 w-48 object-cover rounded-lg border"
                                    />
                                  )}
                                </div>
                              )}
                              <div className="flex items-center space-x-4">
                                <label className={`cursor-pointer px-4 py-2 rounded-lg font-medium ${
                                  uploadingBackground ? 'bg-gray-300 cursor-not-allowed' : 'bg-teal-600 hover:bg-teal-700 cursor-pointer'
                                } text-white`}>
                                  <span>{uploadingBackground ? 'Uploading...' : 'Upload Background'}</span>
                                  <input
                                    type="file"
                                    className="sr-only"
                                    accept="image/jpeg,image/jpg,image/png,image/gif,video/mp4,video/webm,video/ogg"
                                    onChange={(e) => handleBackgroundUpload(e, 'forms')}
                                    disabled={uploadingBackground}
                                  />
                                </label>
                                {editingSection.background_image_url && (
                                  <button
                                    type="button"
                                    onClick={() => handleSectionUpdate('background_image_url', '')}
                                    className="text-red-600 hover:text-red-700"
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                              <p className="text-xs text-teal-600">
                                Upload an image that will appear on the left side of the form
                              </p>
                            </div>
                          </div>
                        );

                      default:
                        return null;
                    }
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default SectionOverview;
